import React from 'react';
import {
  Button,
  Paper,
  Alert,
  CircularProgress,
  Typography,
  Menu,
  MenuItem,
  Collapse,
  Box,
} from '@mui/material';
import SuccessAlert from '../../Common/SuccessAlert';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import AssessmentForm from './AssessmentForm';
import AssessmentResultsDisplay from './AssessmentResultsDisplay';
import ModificationPanel from './ModificationPanel';
import StreamingDisplay from './StreamingDisplay';
import { useAssessmentsPage } from './hooks/useAssessmentsPage';

const AssessmentsPageView: React.FC = () => {
  const p = useAssessmentsPage();

  return (
    <Box sx={{ p: 3 }}>
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}
      </style>

      <SuccessAlert open={p.showSuccessAlert} message={p.successMessage} onClose={p.handleCloseSuccess} />

      {p.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => p.setError(null)}>
          {p.error}
        </Alert>
      )}

      <Paper elevation={3} sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, sm: 0 },
            borderBottom: p.isFormCollapsed ? '1px solid #e0e0e0' : 'none',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#1a202c',
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            Passage Generator
          </Typography>
          <Button
            onClick={() => p.setIsFormCollapsed(!p.isFormCollapsed)}
            startIcon={p.isFormCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            variant="outlined"
            size="small"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {p.isFormCollapsed ? 'Show Form' : 'Hide Form'}
          </Button>
        </Box>

        <Collapse in={!p.isFormCollapsed}>
          <Box sx={{ p: 3 }}>
            <AssessmentForm
              handleSubmit={p.handleSubmit}
              gradeLevel={p.gradeLevel}
              handleGradeLevelChange={p.handleGradeLevelChange}
              loadingGradeLevels={p.loadingGradeLevels}
              availableGradeLevels={p.availableGradeLevels}
              selectedStrand={p.selectedStrand}
              setSelectedStrand={p.setSelectedStrand}
              setSelectedStandards={p.setSelectedStandards}
              availableStrands={p.availableStrands}
              strandFilteredStandards={p.strandFilteredStandards}
              selectedStandards={p.selectedStandards}
              standardLoading={p.standardLoading}
              capitalizeFirstLetter={p.capitalizeFirstLetter}
              topic={p.topic}
              setTopic={p.setTopic}
              language={p.language}
              setLanguage={p.setLanguage}
              isPairedPassage={p.isPairedPassage}
              setIsPairedPassage={p.setIsPairedPassage}
              paragraphCount={p.paragraphCount}
              setParagraphCount={p.setParagraphCount}
              getDefaultParagraphCount={p.getDefaultParagraphCount}
              genre={p.genre}
              setGenre={p.setGenre}
              genreTwo={p.genreTwo}
              setGenreTwo={p.setGenreTwo}
              revisingAndEditing={p.revisingAndEditing}
              setRevisingAndEditing={p.setRevisingAndEditing}
              generateQuestions={p.generateQuestions}
              setGenerateQuestions={p.setGenerateQuestions}
              questionStyle={p.questionStyle}
              setQuestionStyle={p.setQuestionStyle}
              questionMix={p.questionMix}
              setQuestionMix={p.setQuestionMix}
              isStreaming={p.isStreaming}
              isLoading={p.isLoading}
              generateImages={p.generateImages}
              setGenerateImages={p.setGenerateImages}
              imageCount={p.imageCount}
              setImageCount={p.setImageCount}
            />
          </Box>
        </Collapse>
      </Paper>

      {p.currentStatus && (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, backgroundColor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151' }}>
              {p.currentStatus || 'Processing your request...'}
            </Typography>
          </Box>
        </Paper>
      )}

      {(p.isStreaming || p.isChatProcessing) && p.streamingPassages.length > 0 && (
        <StreamingDisplay
          passages={p.streamingPassages}
          questionsContent={p.streamingQuestionsContent}
          answerKeyContent={p.streamingAnswerKeyContent}
          status={p.currentStatus}
          isStreaming={p.isStreaming || p.isChatProcessing}
          isQuestionsComplete={p.isQuestionsComplete}
        />
      )}

      {!p.isStreaming && !p.isChatProcessing && p.assessmentResponse && (
        <>
          <AssessmentResultsDisplay
            assessmentResponse={p.assessmentResponse}
            generationStreamingQuestions={p.generationStreamingQuestions}
            generationStreamingCombinedQuestions={p.generationStreamingCombinedQuestions}
          />

          <ModificationPanel
            chatHistory={p.chatHistory}
            chatMessage={p.chatMessage}
            setChatMessage={p.setChatMessage}
            isChatProcessing={p.isChatProcessing}
            onChatSubmit={p.handleChatSubmit}
            onChatCancel={p.handleChatCancel}
          />

          <Menu anchorEl={p.anchorEl} open={Boolean(p.anchorEl)} onClose={p.handleDownloadClose} id="download-menu">
            <MenuItem onClick={p.handleDownloadPDF}>
              <PictureAsPdfIcon sx={{ mr: 1 }} />
              Download as PDF
            </MenuItem>
            <MenuItem onClick={p.handleDownloadWord}>
              <DescriptionIcon sx={{ mr: 1 }} />
              Download as Word
            </MenuItem>
          </Menu>
        </>
      )}

      {p.assessmentResponse && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ maxWidth: 600, mx: 'auto', display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={p.handlePrint}
              disabled={p.isStreaming || p.isChatProcessing || p.isLoading}
              sx={{
                minWidth: 120,
                py: 1.5,
                px: 3,
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                },
              }}
            >
              {p.isStreaming || p.isChatProcessing ? 'Processing...' : 'Print'}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={p.handleDownloadClick}
              disabled={p.isStreaming || p.isChatProcessing || p.isLoading}
              aria-controls="download-menu"
              aria-haspopup="true"
              sx={{
                minWidth: 120,
                py: 1.5,
                px: 3,
              }}
            >
              Download
            </Button>
            {p.hasUnsavedChanges && (
              <Button
                variant="contained"
                color="success"
                onClick={p.handleManualSave}
                disabled={p.isSaving}
                startIcon={p.isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                sx={{
                  minWidth: 140,
                  py: 1.5,
                  px: 3,
                }}
              >
                {p.isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AssessmentsPageView;
